import { useEffect } from 'react';

import { DARWIN_EXPERIMENT_NAMES } from 'shared/enums/darwinExperimentNames';
import { FeatureFlags } from 'featureFlags/constants';
import { getMParticleDeviceId } from 'tracking/utils/getMparticleDeviceId';
import { getMParticleSessionId } from 'tracking/utils/getMParticleSessionId';
import { logExperimentExposure } from 'experiments/utils';
import { MOBILE_WEB_PDP_EXPERIMENT } from 'productTemplate/enums/newMobileWebPdp';
import { setExperimentsMap } from 'store/slice/pdpSlice';
import { useAbortController } from 'shared/hooks/useAbortController';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useStoreSelector, useStoreDispatch } from 'store/hooks/storeHooks';
import { useWindowSize } from 'shared/hooks/useWindowSize';

const assignGroupAndLogExposure = (
  deviceId: string,
  isAuthenticated: boolean,
  signal,
  sessionId: string,
  userId: string,
  storeDispatch,
) => {
  void (async () => {
    const group = await logExperimentExposure({
      deviceId,
      experimentName: DARWIN_EXPERIMENT_NAMES.MOBILE_WEB_PDP_EXPERIMENT,
      isAuthenticated,
      signal,
      optionalMetaData: { sessionId, userId },
    });

    storeDispatch(
      setExperimentsMap({
        [DARWIN_EXPERIMENT_NAMES.MOBILE_WEB_PDP_EXPERIMENT]: {
          experimentGroup: group?.group?.name || null,
        },
      }),
    );
  })();
};

export const useIsNewMobilePdpAccessible = () => {
  const storeDispatch = useStoreDispatch();
  const { isAuthenticated, user } = useStoreSelector((state) => state.auth);
  const { isMobile } = useWindowSize();
  const { signal } = useAbortController()();
  const isNewMobileWebPdpEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_NEW_MOBILE_WEB_PDP);
  const circumventABTesting = useFeatureFlag(FeatureFlags.WEB_CIRCUMVENT_DARWIN);
  const { experimentsMap } = useStoreSelector((state) => state.pdp);

  const deviceId = getMParticleDeviceId();
  const sessionId = getMParticleSessionId();
  const userId = user?.id || deviceId;

  useEffect(() => {
    // Do not log if already assigned an experiment group/ Desktop / FF not enabled
    if (
      !isNewMobileWebPdpEnabled ||
      !isMobile ||
      !!experimentsMap?.[DARWIN_EXPERIMENT_NAMES.MOBILE_WEB_PDP_EXPERIMENT]?.experimentGroup ||
      !deviceId
    )
      return;

    assignGroupAndLogExposure(deviceId, isAuthenticated, signal, sessionId, userId, storeDispatch);
  }, [
    isMobile,
    isNewMobileWebPdpEnabled,
    experimentsMap,
    deviceId,
    isAuthenticated,
    signal,
    sessionId,
    userId,
    storeDispatch,
  ]);

  const canAccessNewMobilePdp =
    (circumventABTesting && isMobile) ||
    (isMobile &&
      isNewMobileWebPdpEnabled &&
      experimentsMap?.[DARWIN_EXPERIMENT_NAMES.MOBILE_WEB_PDP_EXPERIMENT]?.experimentGroup ===
        MOBILE_WEB_PDP_EXPERIMENT.TEST);

  return canAccessNewMobilePdp;
};
